import { useState } from "react";
import { Check, Edit, X, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { PendingReview } from "@/hooks/usePendingReviews";
import { PendingReviewEditDialog } from "./PendingReviewEditDialog";

interface PendingReviewCardProps {
  review: PendingReview;
  onApprove: (review: PendingReview) => void;
  onReject: (id: string) => void;
  onUpdate: (id: string, rawData: Record<string, unknown>) => void;
  isApproving?: boolean;
  isRejecting?: boolean;
}

export const PendingReviewCard = ({
  review,
  onApprove,
  onReject,
  onUpdate,
  isApproving,
  isRejecting,
}: PendingReviewCardProps) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const rawData = review.raw_data as Record<string, unknown>;

  const title = review.item_type === "material" 
    ? (rawData.name as string) || "Untitled Material"
    : (rawData.title as string) || "Untitled Application";

  const previewFields = review.item_type === "material"
    ? [
        { label: "Family", value: rawData.family },
        { label: "DOI", value: rawData.source_doi },
        { label: "Description", value: rawData.description },
      ]
    : [
        { label: "Category", value: rawData.category },
        { label: "Type", value: rawData.application_type },
        { label: "Description", value: rawData.description },
      ];

  return (
    <>
      <Card className="border-border bg-card">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Badge variant="outline" className="mb-2 font-mono text-xs uppercase">
                {review.item_type}
              </Badge>
              <CardTitle className="font-display text-xl">{title}</CardTitle>
            </div>
          </div>
          {review.source && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ExternalLink className="h-3 w-3" />
              <span className="truncate">{review.source}</span>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-3">
          {previewFields.map((field) => (
            field.value && (
              <div key={field.label}>
                <p className="font-mono text-xs uppercase text-muted-foreground">{field.label}</p>
                <p className="text-sm line-clamp-2">{String(field.value)}</p>
              </div>
            )
          ))}
        </CardContent>

        <CardFooter className="flex gap-2 border-t border-border pt-4">
          <Button
            onClick={() => onApprove(review)}
            disabled={isApproving || isRejecting}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-transform"
          >
            {isApproving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4" />
                Approve & Publish
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            onClick={() => setEditDialogOpen(true)}
            disabled={isApproving || isRejecting}
            className="border-primary text-primary hover:bg-primary/10 hover:scale-105 transition-transform"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Button>
          
          <Button
            variant="outline"
            onClick={() => onReject(review.id)}
            disabled={isApproving || isRejecting}
            className="border-destructive text-destructive hover:bg-destructive/10 hover:scale-105 transition-transform"
          >
            {isRejecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <X className="h-4 w-4" />
                Reject
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      <PendingReviewEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        review={review}
        onSave={(rawData) => {
          onUpdate(review.id, rawData);
          setEditDialogOpen(false);
        }}
      />
    </>
  );
};
